import type { WorkflowSwitchCommand, WorkflowSwitchPreview, WorkflowSwitchResult, WorkflowReconciliation, ThreadCommand } from "./workflow-types";
import type { WorkflowDetail, WorkflowBusiness, WorkflowCatalogCreate, WorkflowCatalogEdit, WorkflowCommand, WorkflowOperation, WorkflowResolution, WorkflowPositions } from "./workflow-types";
import type { OwnerWorkflowSettings } from "./types";
import type { MailboxRefreshView } from "./types";
import type { ConfirmFormCustomerInput } from "./types";
// HttpCockpitClient — the live data client. Talks to core-api over /api on the same origin
// (since #915 the SPA is served at the root of its own host and the API at /api on that same
// host — deliberately same-origin), so the HttpOnly session cookie flows automatically; no
// tokens are handled in JS.
import type { CockpitClient } from "./client";
import type { EmailQaBatchInput, EmailQaDetail, EmailQaRun, EmailQaStatus } from "./email-qa-types";
import type {
  AccountProfile, AnalysisRetryResult, AppConfigInfo, HostEnvironmentInfo, CloseRequestResult, ConsoleEventHandlers, ConsoleLiveEvent, ConversationListOptions, ConversationListPage, UndoDoneResult,
  RestoreRequestResult, TrashRequestResult,
  Assignee, AssigneeRef, AssigneeSetResult,
  CustomerForRequest,
  CustomerUpdateInput, CustomerView, DeliveryResolutionResult, DraftAttachmentCancellationResult, DraftAttachmentMutationResult, DraftResult,
  OverrideFactResult, PositionProposalOptions, PositionsView, PreviousRequestsView, CustomerMailView, RecomputeDecisionResult,
  OverviewMetrics, OverviewMetricsQuery, PresenceView,
  ReplyAcknowledgementResult, RequestActivityPage, RequestDetail, RequestListItem,
  SendResult, SessionUser, SetupState, TenantHealthAlerts, TenantLifecycleResult, TenantRow,
  TenantUsage, UserRow, TeamRow, EmailSignatureState, EmailSignatureValue, EmailSignatureLogo, BusinessLogo,
  UncertainDeliveryAttempt,
  OwnerTenantReadiness,
  MailDomainAddressChangeResult, NeoMailboxAvailability, NeoMailboxChangeResult, NeoMailboxOffer, NeoMailboxProvisionResult,
  PlatformMailboxSecret, PlatformMailboxView, PlatformMailboxWebmailSession,
  PlatformMailDomainKind, PlatformMailDomains,
  MailDomainCheckResult, MailDomainView,
  TrustedForwarderView,
  TenantNeoMailboxView, TenantInvitation, IssuedInvitation, InvitationPreview,
  AcceptInvitationInput,
  StaffInvitation, IssuedStaffInvitation, StaffInvitationPreview,
  TenantMailboxCheckResult, TenantMailboxInput, TenantMailboxRow, TenantSendState,
  FeatureKey, FeatureDefaults, GooglePlacesQuotaState, OwnerTenantFeatures, SetTenantFeatureInput, SetPlanLimitsInput,
  OwnerTenantRuleParameters, UpdateTenantRuleParametersInput, OwnerRuleValueDelegation, UpdateRuleValueDelegationInput,
  BusinessRuleValues, UpdateBusinessRuleValuesInput, RuleValuePreview,
  CatalogArticleList, CatalogImportSummary, TenantCatalogStats,
  TestMailCatalog, TestMailInjectResult,
  DraftFirstViewClaim,
  FiledMessageDetail, FiledMessageList, FiledMessagePromotion, FiledMessageScope, FiledTrashResult,
} from "./types";

const BASE = "/api";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly data?: unknown,
    /** Seconds the server asked us to wait, from `Retry-After` on a 429 (#1163); absent otherwise. */
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** `Retry-After` as delta-seconds, or undefined when absent or not a plain number. */
function retryAfterOf(res: Response): number | undefined {
  const raw = res.headers.get("retry-after");
  if (raw === null) return undefined;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

/**
 * Explicit owner tenant-context (#232, server contract from #231). Module-level because the
 * client is a module singleton. Only ever set for an owner session — the API rejects a context
 * from anyone else (403). The owner plane (/owner/*) is cross-tenant by design and never
 * carries it; SSE gets it as a query parameter because EventSource cannot set headers.
 */
let tenantContext: string | null = null;

export function setTenantContext(tenantId: string | null): void {
  tenantContext = tenantId;
}

export function getTenantContext(): string | null {
  return tenantContext;
}

/**
 * A URL the browser fetches by itself — a download link, an `<img>` inside a message body —
 * cannot carry the `X-Tenant-Id` header this client adds to its own calls. An owner browsing
 * another tenant therefore names it the way the SSE stream already does, as the query parameter
 * the SessionGuard accepts. For a normal operator this is the identity function: sending an
 * explicit context from a non-owner session is rejected by the server on purpose (#231).
 */
export function withTenantContext(url: string): string {
  if (!tenantContext || !url.startsWith("/api/")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}tenant_context=${encodeURIComponent(tenantContext)}`;
}

/**
 * The exact shape the server issues, and nothing looser. `sanitize-html` leaves a literal `"` in
 * text content alone, so a body that merely contains the words `src="/api/requests/…` would let a
 * greedier match run past the end of its own tag and destroy a real image's source further down.
 */
const SERVER_ISSUED_SRC = /src="(\/api\/requests\/[^"/]+\/attachments\/[^"/]+)"/g;

/** The same context, applied to the inline-image sources inside a sanitized message body. */
export function withTenantContextInHtml(html: string): string {
  if (!tenantContext) return html;
  return html.replace(SERVER_ISSUED_SRC, (_match, url: string) => `src="${withTenantContext(url)}"`);
}

/**
 * What the logo store accepts, refused here before a megabyte travels: the signature logo and the
 * header logo (#1989) go into the same store under the same limits. The server checks the
 * content itself; this only spares the round trip.
 */
async function logoUploadBody(file: File): Promise<{ content_type: string; base64: string }> {
  if (!["image/png", "image/jpeg"].includes(file.type) || file.size === 0 || file.size > 512 * 1024) {
    throw new ApiError("SIGNATURE_LOGO_INVALID", 400);
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return { content_type: file.type, base64: btoa(binary) };
}

/**
 * Reading the file takes a moment, and an owner can enter another business meanwhile (#2111).
 * `request()` names the business that is current when it is called, so an upload names its own
 * before the read: it is addressed like every other write, to the business it was started in.
 */
async function uploadLogo<T>(path: string, file: File): Promise<T> {
  const business = tenantContext;
  return request<T>("POST", path, await logoUploadBody(file), undefined, business);
}

/** One platform mailbox's route (#1214). A local part names an account only together with its domain (#1899). */
function platformMailboxPath(localPart: string, domain: PlatformMailDomainKind, action = ""): string {
  return `/owner/platform-mailboxes/${encodeURIComponent(localPart)}${action}?domain=${encodeURIComponent(domain)}`;
}

export async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options?: { signal?: AbortSignal; cache?: RequestCache },
  /** Only a caller that waits before it sends names the business itself; `null` is "none", not "current". */
  business: string | null = tenantContext,
): Promise<T> {
  const hasBody = body !== undefined;
  const context = business && !path.startsWith("/owner/") ? business : null;
  const headers =
    hasBody || context
      ? {
          ...(hasBody ? { "Content-Type": "application/json" } : undefined),
          ...(context ? { "X-Tenant-Id": context } : undefined),
        }
      : undefined;
  const res = await fetch(BASE + path, {
    method,
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
    ...options,
  });
  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    // #240: a quota block carries a structured payload but no `message` — surface it clearly at
    // every action site (send/compose/rule publish). Other error shapes are unchanged; `data`
    // (incl. `code`) is preserved on the ApiError for screens that map it.
    const msg =
      data?.code === "QUOTA_EXCEEDED"
        ? `Quota reached: ${data.quota} (${data.usage}/${data.limit})`
        : (data && (data.message || data.error)) || `HTTP ${res.status}`;
    throw new ApiError(String(msg), res.status, data, retryAfterOf(res));
  }
  return data as T;
}

/**
 * One console stream. `EventSource` reconnects on its own with the server's `retry` hint, which
 * would race the caller's backoff policy — so a broken stream is closed here and handed back as
 * `onError`; the live provider decides when to reopen it.
 */
function openConsoleStream(url: string, handlers: ConsoleEventHandlers): () => void {
  const source = new EventSource(url, { withCredentials: true });
  const forward = (type: ConsoleLiveEvent["type"]) => (event: MessageEvent<string>) => {
    try {
      handlers.onEvent({ type, ...JSON.parse(event.data) } as ConsoleLiveEvent);
    } catch {
      // Ignore a malformed payload; the connection itself is still usable.
    }
  };
  source.addEventListener("console.changed", forward("console.changed"));
  source.addEventListener("console.heartbeat", forward("console.heartbeat"));
  source.onerror = () => {
    source.onerror = null;
    source.close();
    handlers.onError();
  };
  return () => {
    source.onerror = null;
    source.close();
  };
}

export class HttpCockpitClient implements CockpitClient {
  previewWorkflowSwitch(id: string): Promise<WorkflowSwitchPreview> { return request("GET", `/requests/${encodeURIComponent(id)}/workflow/switch-preview`); }
  switchWorkflow(id: string, body: WorkflowSwitchCommand): Promise<WorkflowSwitchResult> { return request("POST", `/requests/${encodeURIComponent(id)}/workflow/switch`, body); }
  reconcileWorkflow(id: string, body: WorkflowReconciliation): Promise<{ ok: true }> { return request("POST", `/requests/${encodeURIComponent(id)}/workflow/reconcile`, body); }
  enrichWorkflow(id: string, expectedRevision: number): Promise<{ changed: boolean; reason?: string }> { return request("POST", `/requests/${encodeURIComponent(id)}/workflow/enrich`, { expected_revision: expectedRevision }); }
  processWorkflow(id: string, body: WorkflowCommand): Promise<WorkflowOperation> {
    return request("POST", `/requests/${encodeURIComponent(id)}/workflow/process`, body, { signal: AbortSignal.timeout(30_000) });
  }
  getWorkflowProcessStatus(id: string, key: string): Promise<{ accepted: boolean }> {
    return request("GET", `/requests/${encodeURIComponent(id)}/workflow/process/${encodeURIComponent(key)}`, undefined, { signal: AbortSignal.timeout(30_000) });
  }
  resolveWorkflowTask(id: string, body: WorkflowResolution): Promise<WorkflowDetail> { return request("POST", `/requests/${encodeURIComponent(id)}/workflow/resolve`, body); }
  getWorkflowBusiness(): Promise<WorkflowBusiness> { return request("GET", "/workflow/business"); }
  updateWorkflowCatalogItem(id: string, body: WorkflowCatalogEdit): Promise<WorkflowBusiness> {
    return request("POST", `/workflow/business/catalog/${encodeURIComponent(id)}`, body);
  }
  createWorkflowCatalogItem(body: WorkflowCatalogCreate): Promise<WorkflowBusiness> {
    return request("POST", "/workflow/business/catalog", body);
  }
  getMailboxRefresh(): Promise<MailboxRefreshView> { return request("GET", "/mailbox-refresh"); }
  requestMailboxRefresh(): Promise<MailboxRefreshView> { return request("POST", "/mailbox-refresh", {}); }
  getEmailQaStatus(): Promise<EmailQaStatus> { return request("GET", "/owner/email-qa/status"); }
  listEmailQaRuns(tenantId: string): Promise<{ runs: EmailQaRun[] }> {
    return request("GET", `/owner/email-qa/tenants/${encodeURIComponent(tenantId)}/runs`);
  }
  getEmailQaRun(tenantId: string, runId: string): Promise<EmailQaDetail> {
    return request("GET", `/owner/email-qa/tenants/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}`);
  }
  startEmailQaBatch(tenantId: string, input: EmailQaBatchInput): Promise<{ runs: EmailQaRun[] }> {
    return request("POST", `/owner/email-qa/tenants/${encodeURIComponent(tenantId)}/runs`, input);
  }
  cancelEmailQaRun(tenantId: string, runId: string): Promise<EmailQaDetail> {
    return request("POST", `/owner/email-qa/tenants/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}/cancel`);
  }

  async me(): Promise<SessionUser | null> {
    try {
      const r = await request<{ user: SessionUser }>("GET", "/auth/me");
      return r.user;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null;
      throw e;
    }
  }
  async login(email: string, password: string): Promise<SessionUser> {
    const r = await request<{ user: SessionUser }>("POST", "/auth/login", { email, password });
    return r.user;
  }
  async logout(): Promise<void> {
    await request<unknown>("POST", "/auth/logout", {});
  }
  getAccountProfile(): Promise<AccountProfile> {
    return request<AccountProfile>("GET", "/me/profile");
  }
  updateAccountProfile(input: { email: string; name: string | null }): Promise<AccountProfile> {
    return request<AccountProfile>("POST", "/me/profile", input);
  }
  changeOwnPassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
    return request<{ ok: true }>("POST", "/me/password", { currentPassword, newPassword });
  }
  getConfig(): Promise<AppConfigInfo> {
    return request<AppConfigInfo>("GET", "/config");
  }
  async getHostEnvironment(): Promise<HostEnvironmentInfo> {
    // /health is the one route outside /api the console reads, so it does not go through request().
    const res = await fetch("/health", { credentials: "same-origin" });
    if (!res.ok) throw new ApiError(`/health answered ${res.status}`, res.status);
    const body: unknown = await res.json();
    const environment = (body as { environment?: unknown } | null)?.environment;
    return { environment: environment === "dev" ? "dev" : "production" };
  }
  subscribeConsoleEvents(handlers: ConsoleEventHandlers): () => void {
    // EventSource cannot set custom headers, so the owner tenant-context travels as a query
    // parameter (the guard accepts both carriers, header first).
    const context = tenantContext ? `?tenant_context=${encodeURIComponent(tenantContext)}` : "";
    return openConsoleStream(`${BASE}/events/console${context}`, handlers);
  }

  subscribeOwnerEvents(handlers: ConsoleEventHandlers): () => void {
    return openConsoleStream(`${BASE}/events/owner`, handlers);
  }

  listConversations(options: ConversationListOptions = {}): Promise<ConversationListPage> {
    const params = new URLSearchParams();
    if (options.group_by) params.set("group_by", options.group_by);
    if (options.queue && options.queue !== "all") params.set("queue", options.queue);
    if (options.q) params.set("q", options.q);
    if (options.status) params.set("status", options.status);
    if (options.assignment && options.assignment !== "all") {
      params.set("assignment", options.assignment);
    }
    if (options.date_from) params.set("date_from", options.date_from);
    if (options.date_to) params.set("date_to", options.date_to);
    if (options.sort && options.sort !== "activity_desc") params.set("sort", options.sort);
    if (options.scope && options.scope !== "active") params.set("scope", options.scope);
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.offset !== undefined) params.set("offset", String(options.offset));
    const query = params.size > 0 ? `?${params.toString()}` : "";
    return request<ConversationListPage>("GET", `/conversations${query}`);
  }
  listRequests(): Promise<RequestListItem[]> {
    return request<RequestListItem[]>("GET", "/requests");
  }
  listThreadTargets(requestId: string, query = "", offset = 0): Promise<import("./types").ThreadTargetPage> {
    const params = new URLSearchParams({ q: query, offset: String(offset) });
    return request("GET", `/requests/${encodeURIComponent(requestId)}/thread-targets?${params}`);
  }
  getPresence(): Promise<PresenceView> {
    return request<PresenceView>("GET", "/presence");
  }
  presenceHeartbeat(): Promise<PresenceView> {
    // No body: whose presence is written is the session's to decide, never the caller's.
    return request<PresenceView>("POST", "/presence/heartbeat");
  }
  getOverviewMetrics(query: OverviewMetricsQuery): Promise<OverviewMetrics> {
    const params = new URLSearchParams({ period: query.period });
    // Only a custom period carries dates. Sending them alongside a named one would invite a
    // server that ignored the word and a console that thought it had not been ignored.
    if (query.period === "custom") {
      if (query.from) params.set("from", query.from);
      if (query.to) params.set("to", query.to);
    }
    return request<OverviewMetrics>("GET", `/overview/metrics?${params.toString()}`);
  }
  listFiledMessages(options: { scope?: FiledMessageScope } = {}): Promise<FiledMessageList> {
    const query = options.scope === "all" || options.scope === "trash" ? `?scope=${options.scope}` : "";
    return request<FiledMessageList>("GET", `/filed-messages${query}`);
  }
  getFiledMessage(id: string): Promise<FiledMessageDetail> {
    return request<FiledMessageDetail>("GET", `/filed-messages/${encodeURIComponent(id)}`);
  }
  recordFiledMessageOpening(id: string): Promise<{ recorded: boolean }> {
    return request("POST", `/filed-messages/${encodeURIComponent(id)}/first-opening`, {});
  }
  promoteFiledMessage(id: string): Promise<FiledMessagePromotion> {
    return request<FiledMessagePromotion>(
      "POST",
      `/filed-messages/${encodeURIComponent(id)}/promote`,
    );
  }
  trashFiledMessage(id: string): Promise<FiledTrashResult> {
    return request<FiledTrashResult>("POST", `/filed-messages/${encodeURIComponent(id)}/trash`, {});
  }
  restoreFiledMessage(id: string): Promise<FiledTrashResult> {
    return request<FiledTrashResult>("POST", `/filed-messages/${encodeURIComponent(id)}/restore`, {});
  }
  getTenantHealthAlerts(): Promise<TenantHealthAlerts> {
    return request<TenantHealthAlerts>("GET", "/tenant/health-alerts");
  }
  getSetupState(): Promise<SetupState> {
    return request<SetupState>("GET", "/setup-state");
  }
  claimDraftFirstView(draftId: string): Promise<DraftFirstViewClaim> {
    return request<DraftFirstViewClaim>(
      "POST",
      `/drafts/${encodeURIComponent(draftId)}/first-view`,
    );
  }
  recordRequestOpening(id: string): Promise<{ recorded: boolean }> {
    return request("POST", `/requests/${encodeURIComponent(id)}/first-opening`, {});
  }
  acknowledgeCurrentDraft(id: string, readinessId: string): Promise<{ recorded: boolean }> {
    return request("POST", `/requests/${encodeURIComponent(id)}/draft-view`, { readiness_id: readinessId });
  }
  getRequest(id: string): Promise<RequestDetail> {
    return request<RequestDetail>("GET", `/requests/${encodeURIComponent(id)}`, undefined, { signal: AbortSignal.timeout(30_000) });
  }
  getRequestActivity(id: string, cursor?: string, signal?: AbortSignal): Promise<RequestActivityPage> {
    const query = cursor ? `?${new URLSearchParams({ cursor })}` : "";
    return request<RequestActivityPage>("GET", `/requests/${encodeURIComponent(id)}/activity${query}`, undefined, { signal });
  }
  listPreviousRequests(id: string, cursor?: string): Promise<PreviousRequestsView> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    const query = params.size > 0 ? `?${params.toString()}` : "";
    return request<PreviousRequestsView>(
      "GET",
      `/requests/${encodeURIComponent(id)}/previous-requests${query}`,
    );
  }
  listCustomerMail(id: string): Promise<CustomerMailView> {
    return request<CustomerMailView>("GET", `/requests/${encodeURIComponent(id)}/customer-messages`);
  }
  getPositions(id: string, options?: PositionProposalOptions): Promise<PositionsView> {
    const params = new URLSearchParams();
    if (options?.occasion) params.set("occasion", options.occasion);
    if (options?.staffingFormat) params.set("staffing_format", options.staffingFormat);
    if (options?.durationHours !== null && options?.durationHours !== undefined) {
      params.set("duration_hours", String(options.durationHours));
    }
    if (options) params.set("include_staffing", String(options.includeStaffing));
    const query = params.size > 0 ? `?${params.toString()}` : "";
    return request<PositionsView>("GET", `/requests/${encodeURIComponent(id)}/positions${query}`, undefined, { signal: AbortSignal.timeout(30_000) });
  }
  listUncertainDeliveries(): Promise<UncertainDeliveryAttempt[]> {
    return request<UncertainDeliveryAttempt[]>("GET", "/delivery/uncertain");
  }
  resolveUncertainDelivery(
    attemptId: string,
    delivered: boolean,
  ): Promise<DeliveryResolutionResult> {
    return request<DeliveryResolutionResult>(
      "POST",
      `/send-attempts/${encodeURIComponent(attemptId)}/resolve`,
      { delivered },
    );
  }
  // #803: assignment is a set an admin edits one principal at a time. There is no "assign to me"
  // and no takeover: both were ways for an operator to put themselves on a request.
  listRequestAssignees(requestId: string): Promise<Assignee[]> {
    return request<Assignee[]>("GET", `/requests/${encodeURIComponent(requestId)}/assignees`);
  }
  addRequestAssignee(requestId: string, principal: AssigneeRef): Promise<AssigneeSetResult> {
    return request<AssigneeSetResult>(
      "POST",
      `/requests/${encodeURIComponent(requestId)}/assignees`,
      { kind: principal.kind, id: principal.id },
    );
  }
  removeRequestAssignee(requestId: string, principal: AssigneeRef): Promise<AssigneeSetResult> {
    return request<AssigneeSetResult>(
      "DELETE",
      `/requests/${encodeURIComponent(requestId)}/assignees/${principal.kind}`
        + `/${encodeURIComponent(principal.id)}`,
    );
  }
  closeRequest(requestId: string, reason?: string, expectedVersion?: number): Promise<CloseRequestResult> {
    return request<CloseRequestResult>("POST", `/requests/${encodeURIComponent(requestId)}/close`,
      { ...(reason === undefined ? {} : { reason }),
        ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }) });
  }
  undoDone(requestId: string, expectedVersion: number): Promise<UndoDoneResult> {
    return request<UndoDoneResult>("POST", `/requests/${encodeURIComponent(requestId)}/undo-done`,
      { expected_version: expectedVersion });
  }
  deleteMessage(requestId: string, messageId: string, expectedVersion: number): Promise<{ message_id: string; status: "deleted" | "pending" }> {
    return request("POST", `/requests/${encodeURIComponent(requestId)}/messages/${encodeURIComponent(messageId)}/delete`,
      { expected_version: expectedVersion });
  }

  trashRequest(requestId: string, expectedVersion: number): Promise<TrashRequestResult> {
    return request<TrashRequestResult>("POST", `/requests/${encodeURIComponent(requestId)}/trash`,
      { expected_version: expectedVersion });
  }
  restoreRequest(requestId: string, expectedVersion: number): Promise<RestoreRequestResult> {
    return request<RestoreRequestResult>("POST", `/requests/${encodeURIComponent(requestId)}/restore`,
      { expected_version: expectedVersion });
  }
  retryAnalysis(requestId: string, expectedVersion: number): Promise<AnalysisRetryResult> {
    return request<AnalysisRetryResult>("POST", `/requests/${encodeURIComponent(requestId)}/analysis-retry`,
      { expected_version: expectedVersion });
  }
  saveMenuOccasion(requestId: string, occasion: PositionProposalOptions["occasion"], expectedRevision: number): Promise<{ ok: true }> {
    return request("POST", `/requests/${encodeURIComponent(requestId)}/menu-occasion`,
      { occasion, expected_request_revision: expectedRevision });
  }
  recomputeDecision(requestId: string): Promise<RecomputeDecisionResult> {
    return request<RecomputeDecisionResult>("POST", `/requests/${encodeURIComponent(requestId)}/recompute`);
  }
  acknowledgeReply(
    requestId: string,
    observedLatestInboundMessageId: string,
  ): Promise<ReplyAcknowledgementResult> {
    return request<ReplyAcknowledgementResult>(
      "POST",
      `/requests/${encodeURIComponent(requestId)}/acknowledge-reply`,
      { observed_latest_inbound_message_id: observedLatestInboundMessageId },
    );
  }

  confirmFormCustomer(requestId: string, body: ConfirmFormCustomerInput): Promise<{ ok: true }> {
    return request("POST", `/requests/${encodeURIComponent(requestId)}/customer/confirm`, body);
  }
  getRequestCustomer(requestId: string): Promise<CustomerForRequest> {
    return request<CustomerForRequest>("GET", `/requests/${encodeURIComponent(requestId)}/customer`);
  }
  updateCustomer(customerId: string, body: CustomerUpdateInput): Promise<CustomerView> {
    return request<CustomerView>("POST", `/customers/${encodeURIComponent(customerId)}/update`, body);
  }

  overrideFact(
    requestId: string,
    body: {
      field_path: string;
      value: unknown;
      reason?: string;
      lock?: boolean;
      confirm_external_address?: boolean;
      expected_request_revision?: number;
      expected_fact_version?: number | null;
    },
  ): Promise<OverrideFactResult> {
    return request<OverrideFactResult>("POST", `/requests/${encodeURIComponent(requestId)}/facts/override`, body);
  }
  confirmFact(requestId: string, fieldPath: string, confirmed: boolean, expectedRequestRevision: number, expectedFactVersion: number): Promise<unknown> {
    return request("POST", `/requests/${encodeURIComponent(requestId)}/facts/confirm`, {
      field_path: fieldPath, confirmed, expected_request_revision: expectedRequestRevision, expected_fact_version: expectedFactVersion,
    });
  }
  lockFact(requestId: string, fieldPath: string, locked: boolean, expectedRequestRevision?: number): Promise<unknown> {
    return request("POST", `/requests/${encodeURIComponent(requestId)}/facts/lock`, {
      field_path: fieldPath,
      locked,
      expected_request_revision: expectedRequestRevision,
    });
  }

  generateDraft(requestId: string, options?: PositionProposalOptions, expectedRevision?: number): Promise<DraftResult> {
    return request<DraftResult>("POST", `/requests/${encodeURIComponent(requestId)}/draft`, {
      ...(expectedRevision === undefined ? {} : { expected_revision: expectedRevision }),
      occasion: options?.occasion ?? null,
      staffing_format: options?.staffingFormat ?? null,
      duration_hours: options?.durationHours ?? null,
      include_staffing: options?.includeStaffing ?? false,
    });
  }
  editDraft(
    draftId: string,
    body: {
      subject?: string;
      body_text?: string;
      body_html?: string | null;
      reply_body_html?: string;
      recipient_addr?: string | null;
      expected_version: number;
    },
  ): Promise<unknown> {
    return request("POST", `/drafts/${encodeURIComponent(draftId)}/edit`, body);
  }
  uploadDraftAttachments(
    draftId: string,
    files: readonly File[],
    expectedVersion: number,
    options: {
      operationId: string;
      confirmReplacements?: boolean;
      signal?: AbortSignal;
      onProgress?: (loaded: number, total: number) => void;
      onUploadComplete?: () => void;
    },
  ): Promise<DraftAttachmentMutationResult> {
    const form = new FormData();
    // Fields precede bytes so the server can reject malformed concurrency input early.
    form.append("upload_operation_id", options.operationId);
    form.append("expected_version", String(expectedVersion));
    form.append("confirm_replacements", String(options.confirmReplacements === true));
    files.forEach((file) => form.append("files", file, file.name));
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BASE}/drafts/${encodeURIComponent(draftId)}/attachments`);
      xhr.withCredentials = true;
      if (tenantContext) xhr.setRequestHeader("X-Tenant-Id", tenantContext);
      xhr.upload.onprogress = (event) => {
        options.onProgress?.(event.loaded, event.lengthComputable ? event.total : 0);
      };
      xhr.upload.onload = () => options.onUploadComplete?.();
      const abort = () => xhr.abort();
      const cleanup = () => options.signal?.removeEventListener("abort", abort);
      options.signal?.addEventListener("abort", abort, { once: true });
      xhr.onerror = () => {
        cleanup();
        reject(new ApiError("Upload failed", 0));
      };
      xhr.onabort = () => {
        cleanup();
        reject(new DOMException("Upload cancelled", "AbortError"));
      };
      xhr.onload = () => {
        cleanup();
        let data: any = null;
        try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { data = null; }
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new ApiError(String(data?.message || data?.error || `HTTP ${xhr.status}`), xhr.status, data));
          return;
        }
        resolve(data as DraftAttachmentMutationResult);
      };
      xhr.send(form);
    });
  }
  cancelDraftAttachmentUpload(
    draftId: string,
    operationId: string,
    expectedVersion: number,
  ): Promise<DraftAttachmentCancellationResult> {
    return request<DraftAttachmentCancellationResult>(
      "POST",
      `/drafts/${encodeURIComponent(draftId)}/attachment-uploads/${encodeURIComponent(operationId)}/cancel`,
      { expected_version: expectedVersion },
    );
  }
  removeDraftAttachment(
    draftId: string,
    attachmentId: string,
    expectedVersion: number,
  ): Promise<DraftAttachmentMutationResult> {
    return request<DraftAttachmentMutationResult>(
      "DELETE",
      `/drafts/${encodeURIComponent(draftId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { expected_version: expectedVersion },
    );
  }
  approveDraft(draftId: string, expectedVersion: number, expectedArtifactHash: string): Promise<unknown> {
    return request("POST", `/drafts/${encodeURIComponent(draftId)}/approve`, {
      expected_version: expectedVersion,
      expected_artifact_hash: expectedArtifactHash,
    });
  }
  rejectDraft(draftId: string, reason?: string): Promise<unknown> {
    return request("POST", `/drafts/${encodeURIComponent(draftId)}/reject`, { reason });
  }
  approveAndSendDraft(draftId: string, command: import("./types").ApproveAndSendCommand): Promise<SendResult> {
    return request("POST", `/drafts/${encodeURIComponent(draftId)}/approve-and-send`, command);
  }
  addCustomerQuestions(draftId: string, expectedVersion: number): Promise<{ added: number }> {
    return request("POST", `/drafts/${encodeURIComponent(draftId)}/add-customer-questions`, { expected_version: expectedVersion });
  }
  sendDraft(draftId: string): Promise<SendResult> {
    return request<SendResult>("POST", `/drafts/${encodeURIComponent(draftId)}/send`, {});
  }

  moveMessage(messageId: string, targetRequestId: string, command?: ThreadCommand): Promise<unknown> {
    return request("POST", `/messages/${encodeURIComponent(messageId)}/move`, {
      target_request_id: targetRequestId, ...command,
    });
  }
  newRequestFromMessage(messageId: string, command?: ThreadCommand): Promise<{ newRequestId?: string }> {
    return request("POST", `/messages/${encodeURIComponent(messageId)}/new-request`, command ?? {});
  }
  mergeInto(requestId: string, targetRequestId: string, command?: ThreadCommand): Promise<{ into?: string }> {
    return request("POST", `/requests/${encodeURIComponent(requestId)}/merge-into`, {
      target_request_id: targetRequestId, ...command,
    });
  }

  listUsers(): Promise<UserRow[]> {
    return request<UserRow[]>("GET", "/users");
  }
  listAssignees(): Promise<UserRow[]> {
    return request<UserRow[]>("GET", "/users?scope=assignees");
  }
  createUser(email: string, password: string, role: string, name: string): Promise<UserRow> {
    return request<UserRow>("POST", "/users", { email, password, role, name });
  }
  setUserPassword(userId: string, password: string): Promise<unknown> {
    return request("POST", `/users/${encodeURIComponent(userId)}/password`, { password });
  }
  setUserRole(userId: string, role: string): Promise<unknown> {
    return request("POST", `/users/${encodeURIComponent(userId)}/role`, { role });
  }
  deleteUser(userId: string): Promise<unknown> {
    return request("DELETE", `/users/${encodeURIComponent(userId)}`);
  }
  // #802: work teams. POST for every mutation (the server declares no @Put), DELETE only for
  // removing a membership; a team itself is archived rather than deleted.
  // #803: the caller's own memberships. Any session may read this; the full list is admin-only.
  listOwnTeamIds(): Promise<{ teamIds: string[] }> {
    return request<{ teamIds: string[] }>("GET", "/teams/mine");
  }
  listTeams(): Promise<TeamRow[]> {
    return request<TeamRow[]>("GET", "/teams");
  }
  createTeam(name: string): Promise<TeamRow> {
    return request<TeamRow>("POST", "/teams", { name });
  }
  renameTeam(teamId: string, name: string, version: number): Promise<{ version: number }> {
    return request("POST", `/teams/${encodeURIComponent(teamId)}/rename`, { name, version });
  }
  archiveTeam(teamId: string, version: number): Promise<{ version: number }> {
    return request("POST", `/teams/${encodeURIComponent(teamId)}/archive`, { version });
  }
  restoreTeam(teamId: string, version: number): Promise<{ version: number }> {
    return request("POST", `/teams/${encodeURIComponent(teamId)}/restore`, { version });
  }
  addTeamMember(teamId: string, userId: string): Promise<unknown> {
    return request("POST", `/teams/${encodeURIComponent(teamId)}/members`, { userId });
  }
  removeTeamMember(teamId: string, userId: string): Promise<unknown> {
    return request(
      "DELETE",
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    );
  }

  setFollowupSendEnabled(enabled: boolean): Promise<unknown> {
    return request("POST", "/settings/followup-send", { enabled });
  }

  getEmailSignature(): Promise<EmailSignatureState> {
    return request<EmailSignatureState>("GET", "/admin/email-signature");
  }
  setEmailSignature(input: EmailSignatureValue): Promise<EmailSignatureState> {
    return request<EmailSignatureState>("POST", "/admin/email-signature", input);
  }
  uploadSignatureLogo(file: File): Promise<EmailSignatureLogo> {
    return uploadLogo<EmailSignatureLogo>("/admin/email-signature/logos", file);
  }
  setBusinessLogo(file: File): Promise<{ logo: BusinessLogo | null }> {
    return uploadLogo<{ logo: BusinessLogo | null }>("/admin/business-logo", file);
  }
  clearBusinessLogo(): Promise<{ logo: BusinessLogo | null }> {
    return request<{ logo: BusinessLogo | null }>("DELETE", "/admin/business-logo");
  }

  listTenants(): Promise<TenantRow[]> {
    return request<TenantRow[]>("GET", "/owner/tenants");
  }
  createTenant(input: { slug: string; displayName: string; prepared?: boolean }): Promise<TenantRow> {
    return request<TenantRow>("POST", "/owner/tenants", input);
  }
  suspendTenant(tenantId: string): Promise<TenantLifecycleResult> {
    return request<TenantLifecycleResult>("POST", `/owner/tenants/${encodeURIComponent(tenantId)}/suspend`, {});
  }
  resumeTenant(tenantId: string): Promise<TenantLifecycleResult> {
    return request<TenantLifecycleResult>("POST", `/owner/tenants/${encodeURIComponent(tenantId)}/resume`, {});
  }
  archiveTenant(tenantId: string): Promise<TenantLifecycleResult> {
    return request<TenantLifecycleResult>("POST", `/owner/tenants/${encodeURIComponent(tenantId)}/archive`, {});
  }
  setOwnerMembership(tenantId: string, attached: boolean): Promise<unknown> {
    return request(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/membership`,
      { attached },
    );
  }
  tenantUsage(tenantId: string): Promise<TenantUsage> {
    return request<TenantUsage>("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/usage`);
  }
  tenantReadiness(tenantId: string): Promise<OwnerTenantReadiness> {
    return request<OwnerTenantReadiness>("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/readiness`);
  }
  getTenantWorkflow(tenantId: string): Promise<OwnerWorkflowSettings> {
    return request("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/workflow`);
  }
  setTenantWorkflow(tenantId: string, input: { engine: "deterministic" | "single_agent"; profile_hash: string; expected_version: number }): Promise<OwnerWorkflowSettings> {
    return request("PUT", `/owner/tenants/${encodeURIComponent(tenantId)}/workflow`, input);
  }
  getTenantFeatures(tenantId: string): Promise<OwnerTenantFeatures> {
    return request<OwnerTenantFeatures>(
      "GET",
      `/owner/tenants/${encodeURIComponent(tenantId)}/features`,
    );
  }
  getBusinessRuleValues(): Promise<BusinessRuleValues> {
    return request("GET", "/settings/rule-values");
  }
  updateBusinessRuleValues(input: UpdateBusinessRuleValuesInput): Promise<BusinessRuleValues> {
    return request("PUT", "/settings/rule-values", input);
  }
  previewBusinessRuleValues(input: UpdateBusinessRuleValuesInput): Promise<RuleValuePreview> {
    return request("POST", "/settings/rule-values/preview", input);
  }
  getTenantRuleParameters(tenantId: string): Promise<OwnerTenantRuleParameters> {
    return request<OwnerTenantRuleParameters>(
      "GET",
      `/owner/tenants/${encodeURIComponent(tenantId)}/rule-parameters`,
    );
  }
  updateTenantRuleParameters(
    tenantId: string,
    input: UpdateTenantRuleParametersInput,
  ): Promise<OwnerTenantRuleParameters> {
    return request<OwnerTenantRuleParameters>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/rule-parameters`,
      input,
    );
  }
  setTenantFeature(
    tenantId: string,
    key: FeatureKey,
    input: SetTenantFeatureInput,
  ): Promise<OwnerTenantFeatures> {
    return request<OwnerTenantFeatures>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/features/${encodeURIComponent(key)}`,
      input,
    );
  }
  clearTenantFeature(tenantId: string, key: FeatureKey): Promise<OwnerTenantFeatures> {
    return request<OwnerTenantFeatures>(
      "DELETE",
      `/owner/tenants/${encodeURIComponent(tenantId)}/features/${encodeURIComponent(key)}`,
    );
  }
  getTenantRuleDelegation(tenantId: string): Promise<OwnerRuleValueDelegation> {
    return request("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/rule-value-delegation`);
  }
  updateTenantRuleDelegation(tenantId: string, input: UpdateRuleValueDelegationInput): Promise<OwnerRuleValueDelegation> {
    return request("PUT", `/owner/tenants/${encodeURIComponent(tenantId)}/rule-value-delegation`, input);
  }
  getFeatureDefaults(): Promise<FeatureDefaults> {
    return request<FeatureDefaults>("GET", "/owner/feature-defaults");
  }
  setFeatureDefaults(defaults: FeatureDefaults): Promise<FeatureDefaults> {
    return request<FeatureDefaults>("POST", "/owner/feature-defaults", defaults);
  }
  getGooglePlacesQuota(): Promise<GooglePlacesQuotaState> {
    return request<GooglePlacesQuotaState>("GET", "/owner/google-places-quota");
  }
  setGooglePlacesQuota(monthlyLimit: number): Promise<GooglePlacesQuotaState> {
    return request<GooglePlacesQuotaState>("POST", "/owner/google-places-quota", { monthlyLimit });
  }
  setPlanLimits(tenantId: string, input: SetPlanLimitsInput): Promise<OwnerTenantFeatures> {
    return request<OwnerTenantFeatures>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/plan-limits`,
      input,
    );
  }
  setTenantSendEnabled(tenantId: string, enabled: boolean): Promise<TenantSendState> {
    return request<TenantSendState>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/send-toggle`,
      { enabled },
    );
  }

  tenantCatalogStats(tenantId: string): Promise<TenantCatalogStats> {
    return request<TenantCatalogStats>("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/catalog`);
  }
  tenantCatalogArticles(
    tenantId: string,
    opts?: { q?: string; limit?: number; offset?: number },
  ): Promise<CatalogArticleList> {
    const params = new URLSearchParams();
    if (opts?.q) params.set("q", opts.q);
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return request<CatalogArticleList>(
      "GET",
      `/owner/tenants/${encodeURIComponent(tenantId)}/catalog/articles${qs ? `?${qs}` : ""}`,
    );
  }
  importTenantCatalog(
    tenantId: string,
    fileBase64: string,
    fileName: string,
  ): Promise<CatalogImportSummary> {
    return request<CatalogImportSummary>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/catalog`,
      { fileBase64, fileName },
    );
  }
  testMailCatalog(): Promise<TestMailCatalog> {
    return request<TestMailCatalog>("GET", "/test-mail/catalog");
  }
  testMailInject(scenario: string): Promise<TestMailInjectResult> {
    return request<TestMailInjectResult>("POST", "/test-mail/inject", { scenario });
  }
  testMailInjectRandom(category?: string): Promise<TestMailInjectResult> {
    return request<TestMailInjectResult>("POST", "/test-mail/inject-random", {
      category: category ?? null,
    });
  }

  listTenantMailboxes(tenantId: string): Promise<TenantMailboxRow[]> {
    return request<TenantMailboxRow[]>("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes`);
  }
  createTenantMailbox(tenantId: string, input: TenantMailboxInput): Promise<TenantMailboxRow> {
    return request<TenantMailboxRow>("POST", `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes`, input);
  }
  updateTenantMailbox(tenantId: string, mailboxId: string, input: TenantMailboxInput): Promise<TenantMailboxRow> {
    return request<TenantMailboxRow>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes/${encodeURIComponent(mailboxId)}`,
      input,
    );
  }
  setTenantMailboxEnabled(
    tenantId: string,
    mailboxId: string,
    enabled: boolean,
    version: number,
  ): Promise<TenantMailboxRow> {
    return request<TenantMailboxRow>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes/${encodeURIComponent(mailboxId)}/${enabled ? "enable" : "disable"}`,
      { version },
    );
  }
  neoMailboxOffer(): Promise<NeoMailboxOffer> {
    return request<NeoMailboxOffer>("GET", "/owner/neo-mailboxes/offer");
  }
  neoMailboxAvailability(localPart: string): Promise<NeoMailboxAvailability> {
    return request<NeoMailboxAvailability>(
      "GET",
      `/owner/neo-mailboxes/availability?localPart=${encodeURIComponent(localPart)}`,
    );
  }
  provisionNeoMailbox(tenantId: string, localPart: string): Promise<NeoMailboxProvisionResult> {
    return request<NeoMailboxProvisionResult>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes/neo-hosted`,
      { localPart },
    );
  }
  changeNeoMailbox(tenantId: string, localPart: string): Promise<NeoMailboxChangeResult> {
    return request<NeoMailboxChangeResult>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes/neo-hosted/change`,
      { localPart },
    );
  }
  changeMailDomainAddress(
    tenantId: string,
    mailDomainId: string,
    localPart: string,
  ): Promise<MailDomainAddressChangeResult> {
    return request<MailDomainAddressChangeResult>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/mail-domains/${encodeURIComponent(mailDomainId)}/mailbox/change`,
      { localPart },
    );
  }
  tenantNeoMailbox(): Promise<TenantNeoMailboxView> {
    return request<TenantNeoMailboxView>("GET", "/neo-mailbox");
  }
  listPlatformMailboxes(): Promise<PlatformMailboxView[]> {
    return request<PlatformMailboxView[]>("GET", "/owner/platform-mailboxes");
  }
  listPlatformMailboxDomains(): Promise<PlatformMailDomains> {
    return request<PlatformMailDomains>("GET", "/owner/platform-mailboxes/domains");
  }
  createPlatformMailbox(input: {
    localPart: string;
    description: string;
    domain: PlatformMailDomainKind;
  }): Promise<PlatformMailboxSecret> {
    return request<PlatformMailboxSecret>("POST", "/owner/platform-mailboxes", input);
  }
  adoptPlatformMailbox(localPart: string, domain: PlatformMailDomainKind): Promise<PlatformMailboxView> {
    return request<PlatformMailboxView>("POST", platformMailboxPath(localPart, domain, "/adopt"));
  }
  resetPlatformMailboxPassword(localPart: string, domain: PlatformMailDomainKind): Promise<PlatformMailboxSecret> {
    return request<PlatformMailboxSecret>("POST", platformMailboxPath(localPart, domain, "/reset-password"));
  }
  openPlatformMailboxInWebmail(localPart: string, domain: PlatformMailDomainKind): Promise<PlatformMailboxWebmailSession> {
    return request<PlatformMailboxWebmailSession>("POST", platformMailboxPath(localPart, domain, "/webmail-session"));
  }
  deletePlatformMailbox(localPart: string, domain: PlatformMailDomainKind): Promise<{ ok: true }> {
    return request<{ ok: true }>("DELETE", platformMailboxPath(localPart, domain));
  }
  listMailDomains(): Promise<MailDomainView[]> {
    return request<MailDomainView[]>("GET", "/mail-domains");
  }
  claimMailDomain(domain: string, localPart?: string): Promise<MailDomainView> {
    return request<MailDomainView>(
      "POST",
      "/mail-domains",
      localPart === undefined ? { domain } : { domain, localPart },
    );
  }
  previousMailDomainAddress(domain: string): Promise<{ emailAddress: string | null }> {
    return request<{ emailAddress: string | null }>(
      "GET",
      `/mail-domains/previous-address?domain=${encodeURIComponent(domain)}`,
    );
  }
  checkMailDomain(id: string): Promise<MailDomainCheckResult> {
    return request<MailDomainCheckResult>("POST", `/mail-domains/${encodeURIComponent(id)}/check`);
  }
  enableMailDomainSending(id: string): Promise<MailDomainView> {
    return request<MailDomainView>("POST", `/mail-domains/${encodeURIComponent(id)}/sending`);
  }
  disableMailDomainSending(id: string): Promise<MailDomainView> {
    return request<MailDomainView>("DELETE", `/mail-domains/${encodeURIComponent(id)}/sending`);
  }
  withdrawMailDomain(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>("DELETE", `/mail-domains/${encodeURIComponent(id)}`);
  }
  disconnectMailDomain(id: string, domain: string): Promise<{ ok: true }> {
    return request<{ ok: true }>("POST", `/mail-domains/${encodeURIComponent(id)}/disconnect`, {
      domain,
    });
  }
  listTrustedForwarders(): Promise<TrustedForwarderView[]> {
    return request<TrustedForwarderView[]>("GET", "/trusted-forwarders");
  }
  addTrustedForwarder(address: string, label: string | null): Promise<TrustedForwarderView> {
    return request<TrustedForwarderView>("POST", "/trusted-forwarders", { address, label });
  }
  removeTrustedForwarder(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>("DELETE", `/trusted-forwarders/${encodeURIComponent(id)}`);
  }
  listInvitations(tenantId: string): Promise<TenantInvitation[]> {
    return request<TenantInvitation[]>("GET", `/owner/tenants/${encodeURIComponent(tenantId)}/invitations`);
  }
  issueInvitation(tenantId: string, input: { email?: string; name?: string }): Promise<IssuedInvitation> {
    return request<IssuedInvitation>("POST", `/owner/tenants/${encodeURIComponent(tenantId)}/invitations`, input);
  }
  revokeInvitation(tenantId: string, invitationId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}/revoke`,
    );
  }
  // The token travels in the body, never the path: it must not reach an access log.
  previewInvitation(token: string): Promise<InvitationPreview> {
    return request<InvitationPreview>("POST", "/onboarding/preview", { token });
  }
  checkInvitationAddress(token: string, localPart: string): Promise<NeoMailboxAvailability> {
    return request<NeoMailboxAvailability>("POST", "/onboarding/availability", { token, localPart });
  }
  acceptInvitation(
    input: AcceptInvitationInput,
  ): Promise<{ ok: true; tenantId: string; mailboxProvisioned: boolean | null }> {
    return request<{ ok: true; tenantId: string; mailboxProvisioned: boolean | null }>(
      "POST",
      "/onboarding/accept",
      input,
    );
  }
  listStaffInvitations(): Promise<StaffInvitation[]> {
    return request<StaffInvitation[]>("GET", "/staff-invitations");
  }
  issueStaffInvitation(input: {
    email: string;
    name?: string;
    role: string;
  }): Promise<IssuedStaffInvitation> {
    return request<IssuedStaffInvitation>("POST", "/staff-invitations", input);
  }
  revokeStaffInvitation(invitationId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      "POST",
      `/staff-invitations/${encodeURIComponent(invitationId)}/revoke`,
    );
  }
  // As with the business flow, the token travels in the body and never in the path.
  previewStaffInvitation(token: string): Promise<StaffInvitationPreview> {
    return request<StaffInvitationPreview>("POST", "/onboarding/staff/preview", { token });
  }
  acceptStaffInvitation(input: {
    token: string;
    name?: string;
    password: string;
  }): Promise<{ ok: true; tenantId: string }> {
    return request<{ ok: true; tenantId: string }>("POST", "/onboarding/staff/accept", input);
  }
  // #1478: the token travels in the body, never in the path, like both invitation flows.
  requestPasswordReset(email: string): Promise<{ ok: true }> {
    return request<{ ok: true }>("POST", "/auth/password-reset/request", { email });
  }
  verifyPasswordReset(token: string): Promise<{ email: string }> {
    return request<{ email: string }>("POST", "/auth/password-reset/verify", { token });
  }
  confirmPasswordReset(input: { token: string; password: string }): Promise<{ ok: true }> {
    return request<{ ok: true }>("POST", "/auth/password-reset/confirm", input);
  }
  checkTenantMailbox(tenantId: string, mailboxId: string): Promise<TenantMailboxCheckResult> {
    return request<TenantMailboxCheckResult>(
      "POST",
      `/owner/tenants/${encodeURIComponent(tenantId)}/mailboxes/${encodeURIComponent(mailboxId)}/check`,
      {},
    );
  }
}
